import type { WorkflowRun } from '../../contracts/routines.types';

import { deriveRunState, runNeedsApproval, type RunState } from './run-state';

export type SessionRoutineGroup = {
  key: string;
  latest: WorkflowRun;
  history: WorkflowRun[];
  runs: WorkflowRun[];
};

function runTime(run: WorkflowRun): number {
  return run.created_at ? Date.parse(run.created_at) : 0;
}

function groupKey(run: WorkflowRun): string {
  if (run.source_workflow_id) return run.source_workflow_id;
  return `run:${run.agent_run_id ?? run.project_id}`;
}

/** One row per routine this visit; extra fires on the same workflow become history. */
export function groupSessionRoutineRuns(runs: WorkflowRun[]): SessionRoutineGroup[] {
  const byKey = new Map<string, WorkflowRun[]>();
  for (const run of runs) {
    const key = groupKey(run);
    const list = byKey.get(key);
    if (list) list.push(run);
    else byKey.set(key, [run]);
  }

  const groups: SessionRoutineGroup[] = [];
  for (const [key, grouped] of byKey) {
    const sorted = [...grouped].sort((a, b) => runTime(b) - runTime(a));
    const [latest, ...history] = sorted;
    if (!latest) continue;
    groups.push({ key, latest, history, runs: sorted });
  }

  return groups.sort((a, b) => runTime(b.latest) - runTime(a.latest));
}

const STATE_URGENCY: Record<RunState, number> = {
  needs_approval: 0,
  running: 1,
  queued: 2,
  failed: 3,
  done: 4,
};

export function pickHeadlineRun(
  runs: WorkflowRun[],
  pendingApprovalRunIds: ReadonlySet<string>
): WorkflowRun {
  const [first, ...rest] = runs;
  if (!first) {
    throw new Error('pickHeadlineRun requires at least one run');
  }
  return rest.reduce((best, run) => {
    const bestState = deriveRunState(best, runNeedsApproval(best, pendingApprovalRunIds));
    const nextState = deriveRunState(run, runNeedsApproval(run, pendingApprovalRunIds));
    const urgencyDelta = STATE_URGENCY[nextState] - STATE_URGENCY[bestState];
    if (urgencyDelta !== 0) return urgencyDelta < 0 ? run : best;
    const bestTime = best.created_at ? Date.parse(best.created_at) : 0;
    const nextTime = run.created_at ? Date.parse(run.created_at) : 0;
    return nextTime > bestTime ? run : best;
  }, first);
}

export function deriveRoutineGroupRowState(
  group: SessionRoutineGroup,
  pendingApprovalRunIds: ReadonlySet<string>,
  surfaceNeedsYou = false
): RunState | 'needs_you' {
  if (group.runs.some((run) => runNeedsApproval(run, pendingApprovalRunIds))) {
    return 'needs_you';
  }
  if (surfaceNeedsYou) {
    return 'needs_you';
  }
  return deriveRunState(pickHeadlineRun(group.runs, pendingApprovalRunIds), false);
}

export function isRoutineGroupCompleted(
  group: SessionRoutineGroup,
  pendingApprovalRunIds: ReadonlySet<string>,
  surfaceNeedsYou = false
): boolean {
  return (
    deriveRoutineGroupRowState(group, pendingApprovalRunIds, surfaceNeedsYou) === 'done'
  );
}
